import { Component, ElementRef, ViewChild, AfterViewInit, Renderer2, OnInit, OnDestroy, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SuiteTechnologiesComponent } from '../../../general/suite-technologies/suite-technologies.component';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';
import { ExploreMoreServicesComponent } from '../explore-more-services/explore-more-services.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface TimelineCard {
  id: number;
  year: string;
  title: string;
  description: string;
  image: string;
}

@Component({
  selector: 'app-data-analytics',
  imports: [CommonModule, SuiteTechnologiesComponent, FaqComponent, ExploreMoreServicesComponent],
  standalone: true,
  templateUrl: './data-analytics.component.html',
  styleUrl: './data-analytics.component.scss'
})
export class DataAnalyticsComponent implements AfterViewInit, OnDestroy, OnInit {
  private jsonLdScript: any;
  @ViewChild('featuresSection') featuresSection!: ElementRef;
  @ViewChild('whyChooseSection') whyChooseSection!: ElementRef;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  isFeaturesVisible = false;
  isWhyChooseVisible = false;
  private observer: IntersectionObserver | null = null;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _renderer: Renderer2,
    private modalService: NgbModal
  ) { }
  goToCaseStudies(): void {

  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }
  dataEngineeringFaqs: FaqItem[] = [
    {
      question: 'What does a Data Engineering Company do?',
      answer: `A Data Engineering Company designs and implements scalable systems that collect, 
      clean, and prepare data for analytics and business intelligence.`
    },
    {
      question: 'How is Cloud Data Engineering different from traditional data management?',
      answer: `Cloud Data Engineering uses scalable, secure, and cost-effective cloud platforms like 
      Azure or AWS for faster and more efficient data processing and storage.`
    },
    {
      question: 'What are the benefits of Data Engineering for my business?',
      answer: `You gain faster insights, reduced operational costs, enhanced decision-making, and a 
      unified data view across all departments.`
    },
    {
      question: 'How long does a data engineering project take?',
      answer: `Project timelines vary from a few weeks to months depending on complexity, integrations, 
      and data volume.`
    }
  ];

  @ViewChild('benefitCardsContainer', { static: false }) private benefitCardsContainer!: ElementRef;

  timelinecards: TimelineCard[] = [
    {
      id: 1,
      year: '2020',
      title: 'Seamless ETL Migration',
      description: `Migrated our client's legacy Informatica ETL to Microsoft Azure Data Factory, reducing high maintenance costs and consolidating over 50 ETL packages into fewer than 20 streamlined pipelines for easier maintenance and robust error handling.`,
      image: '../../../../assets/services/data-analytics/successStories/Seamless-ETL-Migration.png'
    },
    {
      id: 2,
      year: '2023',
      title: 'Vendor Automation Excellence',
      description: `Automated processing of 100+ vendor files with Power BI using a metadata-driven framework, resulting in 78% reduction in onboarding time, real-time analytics, instant alerts, higher cost efficiency, and unparalleled scalability.`,
      image: '../../../../assets/services/data-analytics/successStories/Vendor-Automation-Excellence.png'
    },
    {
      id: 3,
      year: '2025',
      title: 'Reporting Transformation Success',
      description: `Shifted complex financial report migration from Cognos to Power BI, delivering 40+ optimized reports, achieving 60% faster load times, enhanced usability, and scalable, high-performance reporting for multi-company finance teams.`,
      image: '../../../../assets/services/data-analytics/successStories/Reporting-Transformation-Success.png'
    }
    // {
    //   id: 4,
    //   year: '2026',
    //   title: 'Future Vision',
    //   description: 'Leading the way in digital transformation and AI-powered solutions.',
    //   image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300&fit=crop'
    // },
    // {
    //   id: 5,
    //   year: '2027',
    //   title: 'Global Reach',
    //   description: 'Expanding our presence globally with cutting-edge technology solutions.',
    //   image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=400&h=300&fit=crop'
    // }
  ];

  selectedIndex = 0;

  get visibleCards(): Array<{ card: TimelineCard, position: 'top' | 'center' | 'bottom' }> {
    const result = [];
    const totalCards = this.timelinecards.length;

    if (totalCards === 0) return [];

    // Case 1: Single Card
    if (totalCards === 1) {
      result.push({
        card: this.timelinecards[0],
        position: 'center' as const
      });
      return result;
    }

    // Case 2: Multiple Cards (Standard Logic)
    // Top card (only if exists and not the first one)
    if (this.selectedIndex > 0) {
      result.push({
        card: this.timelinecards[this.selectedIndex - 1],
        position: 'top' as const
      });
    }

    // Center card
    result.push({
      card: this.timelinecards[this.selectedIndex],
      position: 'center' as const
    });

    // Bottom card (only if exists and not the last one)
    if (this.selectedIndex < totalCards - 1) {
      result.push({
        card: this.timelinecards[this.selectedIndex + 1],
        position: 'bottom' as const
      });
    }

    return result;
  }

  get showCarouselDots(): boolean {
    return this.timelinecards.length > 3;
  }

  selectCard(position: 'top' | 'center' | 'bottom'): void {
    if (this.timelinecards.length <= 1) return; // Do nothing if single card

    if (position === 'top' && this.selectedIndex > 0) {
      this.selectedIndex--;
    } else if (position === 'bottom' && this.selectedIndex < this.timelinecards.length - 1) {
      this.selectedIndex++;
    }
  }

  goToCard(index: number): void {
    if (index >= 0 && index < this.timelinecards.length) {
      this.selectedIndex = index;
    }
  }

  get canScrollUp(): boolean {
    return this.selectedIndex > 0;
  }

  get canScrollDown(): boolean {
    return this.selectedIndex < this.timelinecards.length - 1;
  }

  whyChooseFeatures = [
    {
      icon: '../../../../assets/services/data-analytics/whyChooseUs/ProvenExpertise.png',
      title: 'Proven Expertise',
      description: 'Years of combined experience in data engineering consulting and enterprise data modernization.'
    },
    {
      icon: '../../../../assets/services/data-analytics/whyChooseUs/End-to-EndSolutions.png',
      title: 'End-to-End Solutions',
      description: 'From data migration to analytics and BI, we manage the full lifecycle.'
    },
    {
      icon: '../../../../assets/services/data-analytics/whyChooseUs/Industry-FocusedApproach.png',
      title: 'Industry-Focused Approach',
      description: 'Proven success across Insurtech, Fintech, Healthcare, E-commerce, and Hi-tech domains.'
    },
    {
      icon: '../../../../assets/services/data-analytics/whyChooseUs/SecurityCompliance.png',
      title: 'Security & Compliance',
      description: 'SOC II certified to ensure enterprise-grade data protection and compliance.'
    }
  ];

  developmentProcess = [
    {
      icon: '../../../../assets/services/data-analytics/developmentProcess/DataDiscoveryAssessment.png',
      title: 'Data Discovery & Assessment',
      description: 'We analyze your existing systems, data quality, and goals.'
    },
    {
      icon: '../../../../assets/services/data-analytics/developmentProcess/ArchitectureDesign.png',
      title: 'Architecture Design',
      description: 'Our experts design a scalable, secure, and cost-efficient architecture using tools like Azure Data Factory, SSIS, and Databricks.'
    },
    {
      icon: '../../../../assets/services/data-analytics/developmentProcess/DataMigrationIntegration.png',
      title: 'Data Migration & Integration',
      description: 'We migrate and unify data across multiple sources while maintaining consistency and integrity.'
    },
    {
      icon: '../../../../assets/services/data-analytics/developmentProcess/ETLPipelineDevelopment.png',
      title: 'ETL & Pipeline Development',
      description: 'Our team automates Extract, Transform, Load (ETL) workflows to ensure data accuracy and efficiency.'
    },
    {
      icon: '../../../../assets/services/data-analytics/developmentProcess/AnalyticsVisualization.png',
      title: 'Analytics & Visualization',
      description: 'We empower decision-makers with Power BI dashboards and SSAS-driven analytics.'
    },
    {
      icon: '../../../../assets/services/data-analytics/developmentProcess/OptimizationSupport.png',
      title: 'Optimization & Support',
      description: 'Continuous monitoring, optimization, and governance to keep your data systems at peak performance.'
    }
  ];

  // Service Overview section data and logic
  services = [
    {
      title: 'Faster Time to Market',
      description: 'We design software that grows with your business and adapts to new technologies.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Secure & Compliant',
      description: 'Our agile development process ensures quick and efficient delivery of your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Innovation at Core',
      description: 'We build robust and secure software that complies with industry standards.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Future-Ready Architecture',
      description: 'We leverage the latest technologies to build innovative and cutting-edge software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'End to End Support',
      description: 'We provide comprehensive support and maintenance services for your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    }
  ];
  selectedServiceIndex = 3; // Default to "Future-Ready Architecture"

  // Pre-defined positions for the icons on the arc and the active position
  private arcPositions = [
    { x: -180, y: 80 },
    { x: -110, y: -20 },
    { x: 0, y: -60 },
    { x: 110, y: -20 },
    { x: 180, y: 80 }
  ];
  private activePosition = { x: 0, y: 150 };

  selectService(index: number): void {
    this.selectedServiceIndex = index;
  }

  getPointTransform(index: number): string {
    let pos;
    if (index === this.selectedServiceIndex) {
      pos = this.activePosition;
    } else {
      pos = this.arcPositions[index];
    }
    return `translate(${pos.x}px, ${pos.y}px)`;
  }

  readMore(): void {
  }
  setupTiltEffect(): void {
    const cards = document.querySelectorAll('.feature-card, .features-img');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate center
        const xc = rect.width / 2;
        const yc = rect.height / 2;

        // Max rotation degrees
        const maxRotate = 10;

        // Calculate rotation based on cursor position
        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate; // Inverse Y axis

        // Apply transform
        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        // Reset to flat state
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        // Smooth entry
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out, background 0.2s ease';
      };

      // Attach Listeners
      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      // Optional: Disable on touch devices for better scrolling
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  setupScrollAnimation(): void {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {

          if (this.featuresSection && entry.target === this.featuresSection.nativeElement) {
            this.isFeaturesVisible = true;
            observer.unobserve(entry.target);
          }

          if (this.whyChooseSection && entry.target === this.whyChooseSection.nativeElement) {
            this.isWhyChooseVisible = true;
            observer.unobserve(entry.target);
          }
        }
      });
    }, {
      threshold: 0.2 // Trigger when 20% visible
    });

    if (this.featuresSection) {
      observer.observe(this.featuresSection.nativeElement);
    }

    if (this.whyChooseSection) {
      observer.observe(this.whyChooseSection.nativeElement);
    }

    this.observer = observer;
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    if (this.observer) {
      this.observer.disconnect();
    }
  }
  injectSchemaMarkup(): void {
    const dataEngineeringSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/data-engineering#webpage",
          "url": "https://www.kalpitatechnologies.com/company-services/data-engineering",
          "name": "Data Engineering Services | ETL, Cloud Data & Power BI | Kalpita Technologies",
          "description": "End-to-end data engineering solutions including ETL pipeline development, data migration, Power BI analytics, and cloud data architecture on Azure.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en-US"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/company-services/data-engineering#breadcrumbs",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.kalpitatechnologies.com/company-services" },
            { "@type": "ListItem", "position": 3, "name": "Data Engineering", "item": "https://www.kalpitatechnologies.com/company-services/data-engineering" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/company-services/data-engineering#service",
          "name": "Data Engineering Services",
          "serviceType": "Data Engineering",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "End-to-end data engineering solutions transforming raw data into business intelligence including cloud data architecture, ETL, and Power BI."
        },
        {
          "@type": "HowTo",
          "@id": "https://www.kalpitatechnologies.com/company-services/data-engineering#process",
          "name": "How Kalpita Builds Your Scalable Data Ecosystem",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Discovery", "text": "We analyze your existing systems, data quality, and business goals." },
            { "@type": "HowToStep", "position": 2, "name": "Architecture", "text": "Design a scalable architecture using Azure Data Factory, SSIS, and Databricks." },
            { "@type": "HowToStep", "position": 3, "name": "Migration", "text": "Migrate and unify data across multiple sources with zero disruption." },
            { "@type": "HowToStep", "position": 4, "name": "ETL Development", "text": "Automate workflows to ensure data accuracy and efficiency." },
            { "@type": "HowToStep", "position": 5, "name": "Visualization", "text": "Empower decision-makers with Power BI dashboards." },
            { "@type": "HowToStep", "position": 6, "name": "Optimization", "text": "Continuous monitoring and optimization for peak performance." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/data-engineering#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What does a Data Engineering Company do?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "A Data Engineering Company designs and implements scalable systems that collect, clean, and prepare data for analytics and business intelligence."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this._renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(dataEngineeringSchema);
    this._renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this._renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    this.setupTiltEffect();
    this.setupScrollAnimation();
    const cards = document.querySelectorAll('.feature-card, .features-img');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate center
        const xc = rect.width / 2;
        const yc = rect.height / 2;

        // Max rotation degrees
        const maxRotate = 10;

        // Calculate rotation based on cursor position
        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate; // Inverse Y axis

        // Apply transform
        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        // Reset to flat state
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        // Smooth entry
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out, background 0.2s ease';
      };

      // Attach Listeners
      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      // Optional: Disable on touch devices for better scrolling
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  getAFreeQuote(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  bookAFreeCall(): void {
    this.getAFreeQuote();
  }
}
